import { bunja } from 'bunja';
import { atom } from 'jotai';

import { CompetitionStatusEnum } from '@flipster/apis/v2/trading-competition/types';
import { JotaiStoreScope } from '@flipster/utils/bunja-jotai';

import { registerCompetitionBunja } from '../../action';
import { competitionApiBunja, competitionBunja } from '../../state';
import { pricePredictionBunja } from './state';
import { nanoToMs } from './utils';

const calcTimeLeftMinutes = (endTime: number | null) => {
  if (!endTime) return 0;
  return Math.max(0, Math.floor((endTime - Date.now()) / (60 * 1000)));
};

export const votingCardBunja = bunja(() => {
  const store = bunja.use(JotaiStoreScope);
  const { pricePredictionAtom, pricePredictionMeAtom, postPredictionMutationAtom, roundInfoAtom } =
    bunja.use(pricePredictionBunja);
  const { isRegisteredAtom } = bunja.use(competitionBunja);
  const { competitionStatusAtom } = bunja.use(competitionApiBunja);
  const { registerCompetition } = bunja.use(registerCompetitionBunja);

  const isUpcomingAtom = atom((get) => get(competitionStatusAtom) === CompetitionStatusEnum.Upcoming);

  const predictionEndTimeAtom = atom((get) => {
    const prediction = get(pricePredictionAtom);
    return nanoToMs(prediction?.pricePredictionEndTime);
  });

  const timeLeftMinutesAtom = atom(0);

  const upRateAtom = atom((get) => Math.round((get(pricePredictionAtom)?.upRate ?? 0.5) * 100));
  const downRateAtom = atom((get) => Math.round((get(pricePredictionAtom)?.downRate ?? 0.5) * 100));

  const predictionEnableAtom = atom((get) => get(pricePredictionAtom)?.predictionEnable ?? false);

  const predictionSideAtom = atom((get) => get(pricePredictionMeAtom)?.today?.predictionSide);

  bunja.effect(() => {
    let interval: ReturnType<typeof setInterval> | null = null;

    const updateTimeLeft = () => {
      const endTime = store.get(predictionEndTimeAtom);
      store.set(timeLeftMinutesAtom, calcTimeLeftMinutes(endTime));
    };

    const startInterval = () => {
      if (interval) clearInterval(interval);
      interval = setInterval(updateTimeLeft, 5 * 1000);
    };

    const unsubscribe = store.sub(predictionEndTimeAtom, () => {
      updateTimeLeft();
      startInterval();
    });

    updateTimeLeft();
    startInterval();

    return () => {
      unsubscribe();
      if (interval) clearInterval(interval);
    };
  });

  return {
    roundInfoAtom,
    timeLeftMinutesAtom,
    upRateAtom,
    downRateAtom,
    predictionEnableAtom,
    predictionSideAtom,
    postPredictionMutationAtom,
    isRegisteredAtom,
    isUpcomingAtom,
    registerCompetition,
  };
});
votingCardBunja.debugLabel = 'votingCardBunja';
